import { useState, useEffect, useMemo, useRef } from 'react'
import { Typography, Box } from '@mui/material'
import {
  StockProjectionExceptionGraphType,
  StockProjectionExceptionPageProps,
  StockProjectionExceptionTableData,
  StockProjectionExceptionURLParams,
  WeekRangeConfig,
} from './StockProjectionException.types'
import { useParams } from 'react-router-dom'
import {
  useStockProjectExceptionsData,
  useStockProjectExceptionsGraphData,
  useStockProjectionWaterfallAvailability,
  useStockProjectionWaterfallData,
} from './StockProjectionException.data'
import { Loader } from 'Common/Loader/Loader'
import { EditWeekRange } from './EditWeekRange.view'
import { SpeGraph } from './SPE.graph'
import { SPETable } from './SPE.table'
import { Legend } from './Legend/Legend.view'
import { LegendType } from './Legend/Legend.type'
import { getFormattedGraphData } from './SPE.methods'
import {
  StockWaterfallGraphs,
  useStockWaterfallWalk,
} from 'Common/Charts/StockWaterfall/StockWaterfall.view'
import { StockWaterfallLegend } from 'Common/Charts/StockWaterfall/StockWaterfallLegend.view'
import { StockWaterfallToggle } from 'Common/Charts/StockWaterfall/StockWaterfallToggle.view'
import {
  DEFAULT_STOCK_WATERFALL_VIEW,
  StockWaterfallView,
} from 'Common/Charts/StockWaterfall/StockWaterfall.constants'
import {
  isLedgerNotAvailable,
  usePlanBuiltByV2Engine,
} from 'Common/Charts/StockWaterfall/stockWaterfall.data'

const DEFAULT_WEEK_RANGE = 10

const legends: LegendType[] = [
  {
    name: 'FD',
    color: '#F59E0B',
    abbreviation: 'Forecast Demand Signal',
    shape: 'square',
  },
  {
    name: 'SITA',
    color: '#EF4444',
    abbreviation: 'Stock In Transit Arrival',
    shape: 'square',
  },
  {
    name: 'PVA',
    color: '#1D4ED8',
    abbreviation: 'Production Volume Arrival',
    shape: 'square',
  },
  {
    name: 'SP',
    color: '#198754',
    abbreviation: 'Stock Policy',
    shape: 'line',
  },
]

export const StockProjectionExceptionPage = ({
  workcentresList,
}: StockProjectionExceptionPageProps) => {
  const [selectedSkuName, setSelectedSkuName] = useState<string | null>(null)
  const [selectedSkus, setSelectedSkus] = useState<string[]>([])
  const [weekRangeConfig, setWeekRangeConfig] = useState<WeekRangeConfig>({
    start: null,
    end: null,
  })
  const [selectedGraphs, setSelectedGraphs] = useState<string[]>([])
  const [hoveredGraphSite, setHoveredGraphSite] = useState<string | null>(null)

  const weeksRef = useRef<
    | null
    | {
        key: string
        value: number
        index: number
      }[]
  >(null)

  const { planId, sourcesiteId } =
    useParams<StockProjectionExceptionURLParams>()

  const {
    data: stockProjectionExceptionTableData,
    isLoading: isStockProjectionExceptionTableDataLoading,
    isFetching: isStockProjectionExceptionTableDataFetching,
  } = useStockProjectExceptionsData({
    planId,
    sourcesiteId,
    weekRangeConfig,
    workcentresList,
  })

  const {
    data: stockProjectionExceptionGraphData,
    isLoading: isStockProjectionExceptionGraphDataLoading,
  } = useStockProjectExceptionsGraphData({
    planId,
    sourcesiteId,
    skuCodes: selectedSkus,
    workcentresList,
  })

  // Stock waterfall v2: a New / Classic switch when at least one selected work
  // centre has a ledger (one primary for no SKU never has one, US149968).
  const [waterfallView, setWaterfallView] = useState<StockWaterfallView>(
    DEFAULT_STOCK_WATERFALL_VIEW
  )
  const { builtByV2Engine, planStartWeek } = usePlanBuiltByV2Engine(planId)
  const { isSuccess: hasLedger } = useStockProjectionWaterfallAvailability({
    planId,
    skuCodes: [],
    workcentresList,
    enabled: builtByV2Engine,
  })
  const {
    data: waterfallRows,
    error: waterfallError,
    isLoading: isWaterfallLoading,
  } = useStockProjectionWaterfallData({
    planId,
    skuCodes: selectedSkus,
    workcentresList,
    enabled: hasLedger && waterfallView === 'new',
  })
  const showWaterfall =
    hasLedger &&
    waterfallView === 'new' &&
    !isLedgerNotAvailable(waterfallError)
  const waterfallSites = useStockWaterfallWalk(
    showWaterfall ? waterfallRows : undefined,
    {
      sites: selectedGraphs,
      startDateKey: weekRangeConfig.start,
      endDateKey: weekRangeConfig.end,
    }
  )

  const weekDropDown = useMemo(() => {
    if (stockProjectionExceptionTableData && weeksRef.current === null) {
      weeksRef.current = stockProjectionExceptionTableData.weeks
    }
    return weeksRef.current ?? []
  }, [stockProjectionExceptionTableData])

  useEffect(() => {
    if (
      weekDropDown?.length &&
      weekRangeConfig.start === null &&
      weekRangeConfig.end === null
    ) {
      const { constrainingHorizon } =
        stockProjectionExceptionTableData.planDetails
      const initialEndVal =
        constrainingHorizon > 0 ? constrainingHorizon : DEFAULT_WEEK_RANGE
      const endVal =
        weekDropDown.length > initialEndVal
          ? weekDropDown[initialEndVal - 1].value
          : weekDropDown[weekDropDown.length - 1].value

      setWeekRangeConfig({
        start: weekDropDown[0].value,
        end: endVal,
      })
    }
  }, [weekDropDown])

  useEffect(() => {
    if (stockProjectionExceptionTableData?.data.length > 0) {
      if (!selectedSkuName) {
        const skuName = stockProjectionExceptionTableData?.data[0].skuName
        setSelectedSkuName(skuName)
      }
      if (selectedGraphs.length === 0) {
        const siteCodes = new Set<string>()
        stockProjectionExceptionTableData.data.forEach(
          (dataRow: StockProjectionExceptionTableData) => {
            siteCodes.add(dataRow.siteCode)
          }
        )
        setSelectedGraphs([...siteCodes])
      }
    }
  }, [stockProjectionExceptionTableData])

  const handleTableRowClick = (data: StockProjectionExceptionTableData[]) => {
    const skuName = data.length === 1 ? data[0]?.skuName : ''
    setSelectedSkuName(skuName)
    setSelectedSkus(data.map((d) => d.skuCode))
  }

  const handleSelectedGraphsChange = (sites: string[]) => {
    setSelectedGraphs(sites)
  }

  const formattedGraphData = useMemo(() => {
    return getFormattedGraphData(
      stockProjectionExceptionGraphData,
      weekRangeConfig,
      selectedGraphs
    )
  }, [stockProjectionExceptionGraphData, weekRangeConfig, selectedGraphs])

  const plotGraphs = (
    graphDataList: StockProjectionExceptionGraphType[],
    y_max: number,
    y_min: number
  ) => {
    return graphDataList
      .filter((mapData: StockProjectionExceptionGraphType) =>
        selectedGraphs.length > 0 ? selectedGraphs.includes(mapData.name) : true
      )
      .map(
        (
          mapData: StockProjectionExceptionGraphType,
          index: number,
          baseArray: StockProjectionExceptionGraphType[]
        ) => {
          return (
            <Box
              key={`spe-graph-${mapData.name}-${index}`}
              data-site-code={mapData.name}
              height={baseArray.length === 1 ? '302px' : '151px'}
              mb={baseArray.length - 1 === index ? 0 : 0.25}
              onMouseEnter={() => setHoveredGraphSite(mapData.name)}
              onMouseLeave={() => setHoveredGraphSite(null)}
            >
              <SpeGraph
                graphData={mapData}
                yAxisRange={[y_min, y_max]}
                oneGraph={baseArray.length === 1}
              />
            </Box>
          )
        }
      )
  }

  const handleWeekRangeChange = (config: WeekRangeConfig) => {
    setWeekRangeConfig(config)
  }

  return (
    <Box display="flex" flexDirection="column" flex="auto">
      <Box>
        {selectedSkus?.length > 0 ? (
          <Box
            sx={{
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '4px',
            }}
          >
            <Box>
              <Box
                sx={{
                  width: '100%',
                  background: (theme) => theme.palette.common.white,
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '10px 20px',
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Box>
                    <Typography variant="h4">
                      {selectedSkuName && `${selectedSkuName}`}
                    </Typography>
                  </Box>
                  <Box>
                    {showWaterfall ? (
                      <StockWaterfallLegend sites={waterfallSites} />
                    ) : (
                      <Legend legends={legends} />
                    )}
                  </Box>
                </Box>
                <Box aria-label="week range and sites container" display="flex">
                  {hasLedger && (
                    <StockWaterfallToggle
                      value={waterfallView}
                      onChange={setWaterfallView}
                    />
                  )}
                  <Box>
                    <EditWeekRange
                      weeks={weekDropDown}
                      config={weekRangeConfig}
                      handleWeekRangeChange={handleWeekRangeChange}
                      loading={
                        isStockProjectionExceptionGraphDataLoading ||
                        isStockProjectionExceptionTableDataLoading
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              marginTop={0.5}
              marginBottom={0}
              sx={{
                minHeight: '310px',
                maxHeight: '310px',
                overflowX: 'hidden',
                overflowY: 'auto',
              }}
            >
              {showWaterfall ? (
                isWaterfallLoading ? (
                  <Loader />
                ) : (
                  <StockWaterfallGraphs
                    sites={waterfallSites}
                    planStartWeek={planStartWeek}
                    onSiteHover={setHoveredGraphSite}
                  />
                )
              ) : isStockProjectionExceptionGraphDataLoading ? (
                <Loader />
              ) : (
                formattedGraphData &&
                selectedGraphs &&
                plotGraphs(
                  formattedGraphData.plotData,
                  formattedGraphData.y_max,
                  formattedGraphData.y_min
                )
              )}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: '360px',
              display: 'flex',
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
          >
            <Typography variant="h3">
              Please select any SKU&apos;s in the below table to view stock
              projection.
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ backgroundColor: '#FFF', marginTop: '5px' }}>
        <SPETable
          data={
            stockProjectionExceptionTableData?.data?.map((tData, index) => {
              tData.index = index
              return tData
            }) ?? []
          }
          handleRowClick={handleTableRowClick}
          isLoading={
            isStockProjectionExceptionTableDataLoading ||
            isStockProjectionExceptionTableDataFetching
          }
          handleSelectedGraphsChange={handleSelectedGraphsChange}
          selectedSkus={selectedSkus}
          selectedSkuName={selectedSkuName}
          hoveredGraphSite={hoveredGraphSite}
        />
      </Box>
    </Box>
  )
}
